import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import {
  prospectsApi,
  ProspectListParams,
  ProspectUpdatePayload,
  ResolveClaimPayload,
  SuppressPayload,
} from '../api/prospectsApi'

// ─── Query Keys ──────────────────────────────────────

const PROSPECT_STATS_KEY = 'prospect-stats'
const PROSPECTS_KEY = 'prospects'
const PROSPECT_KEY = 'prospect'
const PROSPECT_EVENTS_KEY = 'prospect-events'
const PROSPECT_CLAIMS_KEY = 'prospect-claims'

// ─── Reads ───────────────────────────────────────────
//
// No refetchInterval, unlike the support queues. Support polls because a customer is
// waiting on a live ticket; a prospect corpus changes when a script or a webhook touches
// it, and a list that reshuffles under a reviewer mid-read is worse than a slightly stale
// one. Mutations invalidate explicitly instead.

export function useProspectStats() {
  return useQuery({
    queryKey: [PROSPECT_STATS_KEY],
    queryFn: () => prospectsApi.getStats(),
  })
}

export function useProspects(params: ProspectListParams = {}) {
  return useQuery({
    queryKey: [PROSPECTS_KEY, params],
    queryFn: () => prospectsApi.getProspects(params),
  })
}

export function useProspect(idOrHandle: string | number | null) {
  return useQuery({
    queryKey: [PROSPECT_KEY, idOrHandle],
    queryFn: () => prospectsApi.getProspect(idOrHandle!),
    enabled: !!idOrHandle,
  })
}

export function useProspectEvents(idOrHandle: string | number | null) {
  return useQuery({
    queryKey: [PROSPECT_EVENTS_KEY, idOrHandle],
    queryFn: () => prospectsApi.getEvents(idOrHandle!),
    enabled: !!idOrHandle,
  })
}

// ─── Writes ──────────────────────────────────────────

export function useUpdateProspect() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({
      idOrHandle,
      payload,
    }: {
      idOrHandle: string | number
      payload: ProspectUpdatePayload
    }) => prospectsApi.updateProspect(idOrHandle, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PROSPECT_KEY, variables.idOrHandle] })
      queryClient.invalidateQueries({ queryKey: [PROSPECTS_KEY] })
      queryClient.invalidateQueries({ queryKey: [PROSPECT_EVENTS_KEY, variables.idOrHandle] })
      toast({ title: 'Prospect updated' })
    },
    onError: (error: unknown) => {
      // A suppressed prospect returns 409 PROSPECT_SUPPRESSED rather than accepting the
      // edit. That is a decision already made, not a validation slip, so say so plainly
      // instead of showing a generic failure.
      const status = (error as { response?: { status?: number } })?.response?.status
      toast({
        title: status === 409 ? 'This prospect is suppressed' : 'Update failed',
        description:
          status === 409
            ? 'Suppression is terminal. Edits are refused to keep the takedown honest.'
            : (error as Error)?.message,
        variant: 'destructive',
      })
    },
  })
}

export function useSuppressProspect() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({
      idOrHandle,
      payload,
    }: {
      idOrHandle: string | number
      payload: SuppressPayload
    }) => prospectsApi.suppress(idOrHandle, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PROSPECT_KEY, variables.idOrHandle] })
      queryClient.invalidateQueries({ queryKey: [PROSPECTS_KEY] })
      queryClient.invalidateQueries({ queryKey: [PROSPECT_EVENTS_KEY, variables.idOrHandle] })
      queryClient.invalidateQueries({ queryKey: [PROSPECT_STATS_KEY] })
      toast({
        title:
          variables.payload.scope === 'contact'
            ? 'Contact suppressed'
            : variables.payload.scope === 'publishing'
              ? 'Publishing suppressed'
              : 'Publishing and contact suppressed',
      })
    },
    onError: (error: Error) => {
      toast({ title: 'Suppression failed', description: error.message, variant: 'destructive' })
    },
  })
}

// ─── Claims ──────────────────────────────────────────

export function useClaims(status = 'manual') {
  return useQuery({
    queryKey: [PROSPECT_CLAIMS_KEY, status],
    queryFn: () => prospectsApi.getClaims(status),
  })
}

export function useResolveClaim() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ claimId, payload }: { claimId: number; payload: ResolveClaimPayload }) =>
      prospectsApi.resolveClaim(claimId, payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PROSPECT_CLAIMS_KEY] })
      queryClient.invalidateQueries({ queryKey: [PROSPECTS_KEY] })
      queryClient.invalidateQueries({ queryKey: [PROSPECT_STATS_KEY] })
      toast({
        title:
          variables.payload.decision === 'approve'
            ? `Page transferred${data.chapters ? ` — ${data.chapters} chapters` : ''}`
            : 'Claim rejected',
      })
    },
    onError: (error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response?.status
      toast({
        title: status === 409 ? 'Already completed' : 'Could not resolve claim',
        description:
          status === 409
            ? 'This claim has already been verified — the page is with its artist.'
            : (error as Error)?.message,
        variant: 'destructive',
      })
    },
  })
}
